import { sha256Hex } from "@opulancia/deposit-core";
import { strFromU8, strToU8, unzipSync, zipSync, type Zippable } from "fflate";
import { imageRefs, normalizeDoc, type StudioDoc } from "./model.js";

/** The editable project as one file (projet.json and its images), to transfer or recover it. */
export const PROJECT_FILE_FORMAT = "opulancia-atelier-fichier/1";
const MAX_PROJECT_FILE = 200 * 1024 * 1024;

/** Names of the exported slides: 01.png, 02.png… (the cover first). */
export const slideFileName = (index: number) => `${String(index + 1).padStart(2, "0")}.png`;

export function slug(name: string): string {
  const s = name
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  return s || "carrousel";
}

const EXT: Record<string, string> = { "image/jpeg": "jpg", "image/png": "png", "image/webp": "webp" };

export async function projectFile(name: string, doc: StudioDoc, image: (sha256: string) => Promise<Blob>, source?: { id: string; version: number }): Promise<Blob> {
  const entries: Zippable = {
    "projet.json": strToU8(JSON.stringify({ format: PROJECT_FILE_FORMAT, name, exported_at: new Date().toISOString(), source: source ?? null, doc }, null, 2)),
  };
  for (const ref of imageRefs(doc)) entries[`images/${ref.sha256}.${EXT[ref.type] ?? "jpg"}`] = [new Uint8Array(await (await image(ref.sha256)).arrayBuffer()), { level: 0 }];
  return new Blob([zipSync(entries) as Uint8Array<ArrayBuffer>], { type: "application/zip" });
}

export interface ImportedProject {
  name: string;
  doc: StudioDoc;
  images: Map<string, Blob>;
}

/** Reads a project file; every image must be present and match its sha256. */
export async function readProjectFile(file: Blob): Promise<ImportedProject> {
  if (file.size > MAX_PROJECT_FILE) throw new Error("Fichier de projet trop lourd (200 Mo au plus).");
  let entries: Record<string, Uint8Array>;
  try {
    entries = unzipSync(new Uint8Array(await file.arrayBuffer()));
  } catch {
    throw new Error("Ce fichier n'est pas un fichier de projet de l'Atelier (ZIP illisible).");
  }
  const json = entries["projet.json"];
  if (!json) throw new Error("Ce ZIP ne contient pas de projet de l'Atelier (projet.json absent).");
  let raw: { format?: unknown; name?: unknown; doc?: unknown };
  try {
    raw = JSON.parse(strFromU8(json));
  } catch {
    throw new Error("projet.json illisible.");
  }
  if (raw.format !== PROJECT_FILE_FORMAT) throw new Error(`Format de fichier inconnu : ${String(raw.format)}.`);
  const doc = normalizeDoc(raw.doc);
  const images = new Map<string, Blob>();
  for (const ref of imageRefs(doc)) {
    const path = Object.keys(entries).find((p) => p.startsWith(`images/${ref.sha256}.`));
    if (!path) throw new Error(`Image manquante dans le fichier : ${ref.sha256.slice(0, 12)}…`);
    const bytes = entries[path]!;
    if ((await sha256Hex(bytes)) !== ref.sha256) throw new Error(`Image altérée dans le fichier : ${ref.sha256.slice(0, 12)}…`);
    images.set(ref.sha256, new Blob([bytes as Uint8Array<ArrayBuffer>], { type: ref.type }));
  }
  const name = typeof raw.name === "string" && raw.name.trim() ? raw.name.trim().slice(0, 100) : "Projet importé";
  return { name, doc, images };
}
